import type {
  RevenueCloseInput,
  RevenueLedgerCompareQuery,
  RevenueLedgerMonthlyQuery,
} from '@cortex/domain-ac-mens-hub-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 売上集計（定義の版つき台帳）の取得と、月の締め。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上ページの台帳・日次・版の比較・要対応・定義・締めの状態を取得し、月の締めを送る。
 * @graph-connects rpcClient [calls] revenueLedger 手続きの呼び出し
 */

/**
 * 台帳の取得結果は毎朝1回しか変わらないため、画面を行き来する間は取り直さない。
 * @graph-connects none
 */
const LEDGER_STALE_TIME_MS = 5 * 60_000;

/**
 * 台帳の月次。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 版と月の範囲を指定し、最新の計算の月×勘定・プラン・内訳の金額を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.monthly 呼び出し
 */
export function useLedgerMonthly(query: RevenueLedgerMonthlyQuery, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerMonthly(query.version, query.fromMonth, query.toMonth),
    queryFn: () => rpcClient.revenueLedger.monthly(query),
    staleTime: LEDGER_STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

/**
 * 正式版の日次（前日からの増加）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日付の範囲を指定し、正式版の当月累計の前日からの増加を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.daily 呼び出し
 */
export function useLedgerDaily(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerDaily(from, to),
    queryFn: () => rpcClient.revenueLedger.daily({ from, to }),
    staleTime: LEDGER_STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

/**
 * 版の比較。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 2つの版と月の範囲を指定し、対応表の行ごとの値・差額・注記を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.compare 呼び出し
 */
export function useLedgerCompare(query: RevenueLedgerCompareQuery, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerCompare(
      query.leftVersion,
      query.rightVersion,
      query.fromMonth,
      query.toMonth,
    ),
    queryFn: () => rpcClient.revenueLedger.compare(query),
    staleTime: LEDGER_STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

/**
 * 要対応（正式版）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 最新の計算の検算・検知の違反と締め後の差を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.checks 呼び出し
 */
export function useLedgerChecks(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerChecks('v0.1'),
    queryFn: () => rpcClient.revenueLedger.checks({ version: 'v0.1' }),
    staleTime: LEDGER_STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

/**
 * 定義の版の一覧と本文。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 登録されている定義の版の7項目と正本の本文を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.definitions 呼び出し
 */
export function useLedgerDefinitions(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerDefinitions,
    queryFn: () => rpcClient.revenueLedger.definitions(),
    staleTime: LEDGER_STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

/**
 * 月ごとの締めの状態（正式版）。締めた直後に取り直すため、ほかの取得より短く保つ。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月の範囲を指定し、月ごとの締めの状態と締め後の差を取得する。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.closings 呼び出し
 */
export function useLedgerClosings(fromMonth: string, toMonth: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.revenue.ledgerClosings('v0.1', fromMonth, toMonth),
    queryFn: () => rpcClient.revenueLedger.closings({ version: 'v0.1', fromMonth, toMonth }),
    retry: false,
    enabled,
  });
}

/**
 * 月を締める・締め直す。成否にかかわらず台帳の取得をすべて取り直す（締めた月は月次・日次の値が
 * 締めた計算の値に切り替わり、締め後の差も消える。先に締められた・計算が更新された 409 のときも、
 * 最新の締めの状態と計算を見せ直す）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 確認モーダルで見せた計算を月の確定値として送り、月次・日次・締めの状態・要対応を最新にする。
 * @graph-connects rpcClient [calls] oRPC revenueLedger.close 呼び出し
 */
export function useCloseLedgerMonth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevenueCloseInput) => rpcClient.revenueLedger.close(input),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.revenue.ledgerAll });
    },
  });
}
